import debounce from 'lodash/debounce';

const errorMargin = 10; // There may be slight differences, perhaps just a fraction of pixel differences when computing these offsets, due to borders and paddings and margins, oh my... Difference larger than this value is assummed to be an actual tab.
const tabSelector = '.model-nav__container'; // Which classes should be considered tab containers

/**
 * Adds horizontal scrollers to tab layouts that are too wide to display on mobile devices.
 */
class ScrollableTabs {
  /**
   * Constructor. Starts everything up if tabs are found
   */
  constructor() {
    // Binding this to methods
    this.initTabElement = this.initTabElement.bind(this);
    this.initAllElements = this.initAllElements.bind(this);
    this.activateTab = this.activateTab.bind(this);

    // Find all tab heading elements on the page that need scrolling ability
    this.tabElemenets = [];
    document.querySelectorAll(tabSelector).forEach(element => this.tabElemenets.push(element));

    if (this.tabElemenets.length) {
      // Init all
      this.initAllElements();

      let windowSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };

      // Observe layout changes
      window.addEventListener('resize', debounce(() => {
        if (window.innerWidth !== windowSize.width || window.innerHeight !== windowSize.height) {
          windowSize.width = window.innerWidth;
          windowSize.height = window.innerHeight;

          this.initAllElements();
        }
      }, 500));
    }
  }

  /**
   * initAllElements is called initially by constructor, or by resize eventlistener in cases of layout change.
   */
  initAllElements() {
    this.tabElemenets.map(this.initTabElement);
  }

  /**
   * initTabElement initializes a single tab element
   * @param {HTMLElement} container contains a tab element. Initializes lots of things regarding the left and right scrollers.
   */
  initTabElement(container) {
    // Container holds the heading element and buttons on both sides.
    const element = container.querySelector('.tabs__headings');
    if (!element) return;
    element.scrollTo({ left: 0 });

    const leftScroll = container.querySelector('.tabs_scroller_left');
    const rightScroll = container.querySelector('.tabs_scroller_right');
    const needScrolling = this.isScrollingNeeded(element);
    if (needScrolling) {
      // Enrich element object with additional info on the tab positions
      element.tabs = [];

      const scrollWindow = element.clientWidth;
      const initialOffset = element.children[0].getBoundingClientRect().x;
      let smallestWindowRatio = 1; // window ratio 1 => a tab fits 1:1 in the viewable area, 0.5 => two tabs fit in the area...
      for (let tab of element.children) {
        const rect = tab.getBoundingClientRect();
        smallestWindowRatio = Math.min(rect.width / scrollWindow, smallestWindowRatio);
        tab.originalXPosition = Math.round(rect.x - initialOffset);
        element.tabs.push(tab);
      }
      this.activateTabOn = smallestWindowRatio > 0.6;
      element.activateTab = this.activateTab;
      element.stepTab = this.stepTab;

      leftScroll.stepBack = this.stepBack;
      rightScroll.stepForward = this.stepForward;

      // Pass the scrollable element reference for the buttons
      leftScroll.scrollTarget = element;
      rightScroll.scrollTarget = element;

      leftScroll.addEventListener('click', this.stepBack);
      rightScroll.addEventListener('click', this.stepForward);

      leftScroll.classList.remove('hide');
      rightScroll.classList.remove('hide');
    } else {
      leftScroll.classList.add('hide');
      rightScroll.classList.add('hide');
    }

    this.activateTab(element.children[0]);
  }

  /**
   * activateTab activates via click the selected tab if needed
   * @param {HTMLElement} tab either a clickable element, or a container of a clickable buttoon or anchor.
   */
  activateTab(tab) {
    if (!this.activateTabOn) return; // In some cases we don't want to trigger tab activation here, because there are more tabs in the view.
    const nestedControl = tab.querySelector('a, button');
    if (nestedControl && nestedControl.click()) {
      // See if there is a nested button or a that needs to be clicked
      nestedControl.click();
    } else if (tab && tab.click) {
      // Just attempt to click this element
      tab.click();
    } else {
      // Doesn't even seem to be a clickable element?
      console.log('activateTab', tab);
    }
  }

  /**
   * stepBack moves the tab bar back one step, or loops around
   */
  stepBack() {
    const { tabs, activateTab } = this.scrollTarget;
    const currentPos = this.scrollTarget.scrollLeft;
    const maxScroll = this.scrollTarget.scrollWidth - this.scrollTarget.clientWidth;

    let nextPosition = maxScroll; // Default to max position (loops back to this position when reaches the end)
    if (currentPos > errorMargin) {
      // tabs is cloned by [...tabs] to prevent reverse() from mutating the original array
      const prevTab = [...tabs].reverse().find(tab => tab.originalXPosition < currentPos - errorMargin);
      nextPosition = prevTab ? prevTab.originalXPosition : maxScroll;
      activateTab(prevTab || tabs[tabs.length - 1]);
    } else {
      activateTab(tabs[tabs.length - 1]);
    }

    this.scrollTarget.scrollTo({ left: nextPosition, behavior: 'smooth' });
  }

  /**
   * stepForward moves the tab bar forward one step, or loops around
   */
  stepForward() {
    const { tabs, activateTab } = this.scrollTarget;
    const currentPos = this.scrollTarget.scrollLeft;
    const maxScroll = this.scrollTarget.scrollWidth - this.scrollTarget.clientWidth;

    let nextPosition = 0; // Default to zero position (loops back to this position when reaches the end)
    if (Math.abs(currentPos - maxScroll) > errorMargin) {
      const nextTab = tabs.find(tab => tab.originalXPosition > currentPos + errorMargin);
      nextPosition = nextTab ? nextTab.originalXPosition : 0;
      activateTab(nextTab || tabs[0]);
    } else {
      activateTab(tabs[0]);
    }

    this.scrollTarget.scrollTo({ left: nextPosition, behavior: 'smooth' });
  }

  /**
   * isScrollingNeeded Checks if the tabs take more space than can fit to space abvailable
   * @param {HTMLElement} element container of the tabs.
   * @returns {boolean} true if scroller should be displayed
   */
  isScrollingNeeded(element) {    
    if (!element) return false;
    // Get container visible width & content width
    const containerWidth = element.clientWidth;
    const contentWidth = element.scrollWidth;
    // if content width > container width -> scrolling is needed.
    return contentWidth > containerWidth;
  }

  /**
   * init Static initializer of the class. This
   */
  static init() {
    const tabs = document.querySelectorAll(tabSelector);
    if (tabs) new ScrollableTabs();
  }
}

export default ScrollableTabs;
